import { h, Component, Fragment } from "preact";
import { useState, useEffect, useCallback } from "preact/hooks";
import { MacroMessageDescriptor, msg, t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { PokerService, PokerAccediResponse } from "../../../services/poker"; // Percorso aggiornato
import FullSite from "./full-site"; // Assumendo che sia .jsx o .tsx
import MobileSite from "../mobile-pages/full-site"; // Assumendo che sia .jsx o .tsx
import formatCurrency from "../../../services/currency";
import PageDataFeed from "../../../services/page-data-feed";
import Config from "../config";
import User from "../../../services/user";
import Observer from "../utils/observer";
// Importa altri componenti UI necessari, ad esempio per lo slider o i bottoni se sono personalizzati

// Interfaccia per lo stato del componente
interface PokerPageState {
    balance: number | null;
    isLoading: boolean;
    error: string | null;
    transferAmount: number;
}

export function PokerPageDesktop() {
    return <FullSite>
        <PokerPage onPleaseLogin={() => Observer.notify('open-user-login')} />
    </FullSite>
}
export function PokerPageMobile() {
    return <MobileSite>
        <PokerPage onPleaseLogin={() => location.hash = "register"} />
    </MobileSite>
}

export default function PokerPage({ onPleaseLogin }: { onPleaseLogin: () => void }) {
    const [pokerInfo, setPokerInfo] = useState<PokerAccediResponse | null>(null);
    const [balance, setBalance] = useState<number | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<MacroMessageDescriptor | null>(null);
    const [transferAmount, setTransferAmount] = useState<number>(0); // Importo per deposito/prelievo

    const fetchPokerInfo = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const info = await PokerService.getSessionInfo();
            if (info.wstoken == null) {
                onPleaseLogin();
                info.wsbalancecurrency = 0;
            }
            setPokerInfo(info);
            setBalance(info.wsbalancecurrency); // Saldo iniziale dal servizio
        } catch (err) {
            console.error("Errore nel caricare le informazioni del poker:", err);
            setError(msg`Impossibile caricare le informazioni del poker. Riprova più tardi.`);
        } finally {
            setIsLoading(false);
        }
    }, [User.isLogged()]);

    useEffect(() => {
        fetchPokerInfo();
    }, [fetchPokerInfo]);

    const handleTransfer = async (isDeposit: boolean) => {
        setIsLoading(true);
        setError(null);
        const amountToTransfer = isDeposit ? transferAmount : -transferAmount;
        try {
            const result = await PokerService.balanceTransfer(amountToTransfer);
            if (result.status == 'failed')
                throw result;

            setBalance(result.balance);
            // Potresti voler mostrare un messaggio di successo
            setTransferAmount(0); // Resetta l'importo
        } catch (err: any) { // Specificare 'any' o un tipo più preciso se disponibile per 'err'
            console.error("Errore durante il trasferimento del saldo:", err);
            let errorMessage = msg`Trasferimento fallito. Riprova.`; // Messaggio di default
            if (err && err.code) {
                switch (err.code) {
                    case 'agency-not-allowed':
                        errorMessage = msg`Agenzia non autorizzata.`;
                        break;
                    case 'login-required':
                        errorMessage = msg`Accesso richiesto. Effettua il login e riprova.`;
                        break;
                    case 'api-login-error':
                        errorMessage = msg`Errore di login API. Contatta l'assistenza.`;
                        break;
                    case 'api-empty-wallet':
                        errorMessage = msg`Il portafoglio API è vuoto.`;
                        break;
                    case 'api-withdrawal-fail':
                        errorMessage = msg`Prelievo API fallito.`;
                        break;
                    case 'api-deposit-fail':
                        errorMessage = msg`Deposito API fallito.`;
                        break;
                    case 'insufficient-funds':
                        errorMessage = msg`Fondi insufficienti nel portafoglio principale.`;
                        break;
                    case 'transaction-failed':
                        errorMessage = msg`Transazione fallita.`;
                        break;
                    case 'api-insufficient-availability':
                        errorMessage = msg`Disponibilità API insufficiente.`;
                        break;
                    default:
                        // Nessun messaggio specifico, usa quello di default
                        break;
                }
            }
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleAccedi = () => {
        openLobby();
    };

    return (
        <div class="mx-auto p-4 md:min-h-screen text-white min-h-[inherit] bg-cover" style={{ backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(${require('url:../assets/img/casino/pokerbg.png')})`, backgroundRepeat: "repeat" }}> {/* Stile di base per il contenuto */}
            <h1 class="text-3xl font-bold mb-6 text-center"><Trans>Poker Wallet</Trans></h1>

            {isLoading && !error && <p class="text-center"><Trans>Caricamento...</Trans></p>}
            {error && <p class="text-center text-red-500 bg-red-100 border border-red-400 p-3 rounded mb-4">{window.Translation(error)}</p>}

            {!isLoading && pokerInfo && (
                <div class="bg-harif-800 shadow-md rounded-lg p-6 max-w-lg mx-auto">
                    <div class="mb-6">
                        {User.isLogged()
                            ? <p class="text-xl"><Trans>Saldo Poker Attuale</Trans>: <span class="font-semibold text-green-400">{balance !== null ? formatCurrency(balance) : '-'} {Config.currencySymbol}</span></p>
                            : <p class="text-xl text-red-500"><Trans>Please login</Trans></p>}
                    </div>

                    <div class="mb-6">
                        <label htmlFor="transferAmount" class="block text-sm font-medium mb-1"><Trans>Importo da Trasferire:</Trans></label>
                        <input
                            type="number"
                            id="transferAmount"
                            step={0.01}
                            value={transferAmount}
                            onBlur={(e) => setTransferAmount(isNaN(parseFloat((e.target as HTMLInputElement).value)) ? transferAmount : parseFloat((e.target as HTMLInputElement).value))}
                            class="w-full p-2 border border-harif-950 rounded bg-harif-950 text-white focus:ring-blue-500 focus:border-blue-500 placeholder-gray-400"
                            placeholder={`0.00`}
                        />
                        {/* Semplice slider come esempio, potrebbe essere migliorato con una libreria UI */}
                        <input
                            type="range"
                            min="0"
                            max={String(balance !== null && balance > 0 ? balance : 1000)} // Esempio di max, adattare
                            value={transferAmount}
                            onInput={(e) => setTransferAmount(parseFloat((e.target as HTMLInputElement).value))}
                            class="w-full h-2 bg-harif-950 rounded-lg appearance-none cursor-pointer mt-2"
                        />
                    </div>

                    <div class="flex flex-col sm:flex-row justify-around space-y-3 sm:space-y-0 sm:space-x-3 mb-6">
                        <button
                            onClick={() => handleTransfer(true)}
                            disabled={isLoading || transferAmount <= 0}
                            class="w-full sm:w-auto bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 rounded disabled:opacity-50 transition duration-150"
                        >
                            <Trans>Deposita nel Poker Wallet</Trans>
                        </button>
                        <button
                            onClick={() => handleTransfer(false)}
                            disabled={isLoading || transferAmount <= 0 || (balance !== null && transferAmount > balance)}
                            class="w-full sm:w-auto bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded disabled:opacity-50 transition duration-150"
                        >
                            <Trans>Preleva dal Poker Wallet</Trans>
                        </button>
                    </div>

                    <div class="mt-8 text-center">
                        <button
                            onClick={handleAccedi}
                            disabled={isLoading || !pokerInfo?.wstoken}
                            class="w-full sm:w-1/2 bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded disabled:opacity-50 transition duration-150 text-lg"
                        >
                            <Trans>Accedi al Poker</Trans>
                        </button>
                    </div>
                </div>
            )}
            {!isLoading && !pokerInfo && !error && (
                <p class="text-center text-yellow-500"><Trans>Nessuna informazione sul poker disponibile.</Trans></p>
            )}
        </div>
    );
}


const openLobby = () => {
    window.open(Config.softionAPI + "/poker/lobby", "poker-lobby", "width=" + 1200 + ",height=" + 660)
}